import React, { useState } from "react";
import { BackButton, Button, Card, Chip, HeartIcon, MapPinIcon, ShieldIcon, Textarea } from "./ui";

/* -------------------------------------------------------------------------- */
/* DogDetail — deep profile view                                              */
/* -------------------------------------------------------------------------- */

export default function DogDetail({ dog, alreadySent, hasChat, onBack, onSend, onOpenChat }) {
  const [message, setMessage] = useState(
    "Hola, me interesa que nuestros perros se conozcan."
  );
  const images =
    dog.images && dog.images.length ? dog.images : dog.image ? [dog.image] : [];
  const [viewer, setViewer] = useState(null); // index or null
  const touchX = React.useRef(null);

  const openViewer = () => images.length && setViewer(0);
  const nextImg = () => setViewer((i) => (i + 1) % images.length);
  const prevImg = () => setViewer((i) => (i - 1 + images.length) % images.length);

  return (
    <div
      className="min-h-screen bg-[#FBFCFF] pb-32"
      data-testid="dog-detail-screen"
    >
      <div className="relative">
        <button
          type="button"
          onClick={openViewer}
          className="block w-full cursor-zoom-in"
          aria-label="Ampliar fotos"
          data-testid="dog-detail-hero-btn"
        >
          <img
            src={dog.image || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={dog.name}
            className="h-64 w-full object-cover sm:h-72 md:h-[380px]"
          />
        </button>

        {images.length > 1 && (
          <span
            className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs font-bold text-white"
            data-testid="dog-detail-photo-count"
          >
            📷 {images.length} fotos · toca para ver
          </span>
        )}

        <div className="absolute left-4 right-4 top-4 flex items-center justify-between">
          <BackButton onClick={onBack} testId="dog-detail-back-btn" />
          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-[#FF5C5C] shadow-lg"
            aria-label="Favorito"
          >
            <HeartIcon size={20} />
          </button>
        </div>
      </div>

      {viewer !== null && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4"
          data-testid="dog-detail-viewer"
          onClick={() => setViewer(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setViewer(null); }}
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl font-black text-white"
            aria-label="Cerrar visor"
            data-testid="dog-detail-viewer-close"
          >
            ×
          </button>
          <img
            src={images[viewer] || "/dog-placeholder.svg"}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
            alt={`${dog.name} ${viewer + 1}`}
            className="max-h-[80vh] w-auto max-w-full rounded-2xl object-contain"
            data-testid="dog-detail-viewer-img"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { touchX.current = e.changedTouches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchX.current === null || images.length < 2) return;
              const dx = e.changedTouches[0].clientX - touchX.current;
              if (dx < -40) nextImg();
              else if (dx > 40) prevImg();
              touchX.current = null;
            }}
          />
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prevImg(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-2xl font-black text-white"
                aria-label="Foto anterior"
                data-testid="dog-detail-viewer-prev"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); nextImg(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-2xl font-black text-white"
                aria-label="Foto siguiente"
                data-testid="dog-detail-viewer-next"
              >
                ›
              </button>
              <div className="mt-4 flex gap-2" data-testid="dog-detail-viewer-dots">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`h-2 w-2 rounded-full ${i === viewer ? "bg-white" : "bg-white/40"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="mx-auto max-w-3xl px-5">
        <Card className="-mt-5 px-6 pb-6 pt-7 sm:pt-8">
          <div className="min-w-0">
            <div className="flex items-start gap-2">
              <h1 className="min-w-0 break-words font-display text-2xl font-black leading-snug text-[#0B2D4D] sm:text-3xl md:text-4xl">
                {dog.name}
              </h1>
              {dog.verified && (
                <span
                  className="mt-1.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ background: "#67D5B5" }}
                  title="Verificado"
                  aria-label="Verificado"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                </span>
              )}
            </div>
            <p className="mt-1 text-sm font-semibold text-slate-500">
              {dog.breed} · {dog.age} años · {dog.sex}
            </p>
            <div className="mt-3">
              <Chip tone="mint" className="text-sm">
                {dog.available === false ? "No disponible" : "🐾 Disponible para cruce"}
              </Chip>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <InfoBox label="Peso" value={dog.weight} emoji="⚖️" />
            <InfoBox label="Tamaño" value={dog.size} emoji="📏" />
            <InfoBox label="Distancia" value={dog.distance || "—"} emoji="📍" />
            <InfoBox label="Color" value={dog.color || "—"} emoji="🎨" />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Chip tone="mint">{dog.vaccines ? "💉 Vacunas al día" : "Vacunas pendientes"}</Chip>
            <Chip tone={dog.health === "Sin enfermedades" ? "mint" : "yellow"}>
              🩺 {dog.health}
            </Chip>
            {dog.dewormed && <Chip tone="mint">🌿 Desparasitación al día</Chip>}
            {dog.pedigree === "Sí" && <Chip tone="yellow">🏅 Pedigrí</Chip>}
            {dog.lastVetCheck && (
              <Chip tone="blue">📅 Último control: {dog.lastVetCheck}</Chip>
            )}
            {dog.sterilized && <Chip tone="gray">Esterilizado</Chip>}
          </div>

          {/* Temperament */}
          <Section title="Temperamento">
            <div className="flex flex-wrap gap-2">
              {(dog.temperament || []).map((t) => (
                <Chip key={t} tone="navy">{t}</Chip>
              ))}
            </div>
            {dog.notes && (
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                {dog.notes}
              </p>
            )}
            {dog.compatibility && (
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                <span className="font-bold text-[#0B2D4D]">Compatibilidad: </span>
                {dog.compatibility}
              </p>
            )}
          </Section>

          {/* Owner */}
          <div className="mt-5 rounded-3xl bg-emerald-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm">
                <ShieldIcon size={20} />
              </div>
              <div>
                <p className="font-display text-lg font-black text-[#0B2D4D]">
                  {dog.owner}
                </p>
                <p className="mt-0.5 text-sm text-slate-600">
                  {dog.verified
                    ? "Identidad verificada · acepta mantener la comunicación dentro de PawCita."
                    : "Identidad pendiente de verificación."}
                </p>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
                  <MapPinIcon size={12} />
                  {dog.location || "Ubicación no indicada"}
                </div>
              </div>
            </div>
          </div>

          {/* Send request / open chat */}
          <div className="mt-6 rounded-3xl border border-slate-100 bg-white p-4">
            {hasChat ? (
              <>
                <Button
                  variant="primary"
                  onClick={onOpenChat}
                  testId="dog-detail-open-chat-btn"
                  className="w-full py-4 text-base"
                >
                  <span aria-hidden="true">🐾</span>
                  <span>Abrir conversación</span>
                </Button>
                <p className="mt-3 flex items-center justify-center gap-2 text-center text-xs text-slate-400">
                  <span>💬</span> Ya tienes una conversación con este perrito.
                </p>
              </>
            ) : (
              <>
                <Textarea
                  label="Mensaje opcional"
                  rows={3}
                  placeholder="Hola, me interesa que nuestros perros se conozcan."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  testId="dog-detail-message-input"
                />
                <Button
                  variant={alreadySent ? "mint" : "primary"}
                  onClick={() => onSend(dog, message)}
                  disabled={alreadySent}
                  testId="dog-detail-send-btn"
                  className="mt-4 w-full py-4 text-base"
                  title={alreadySent ? "Movimiento de colita enviado" : "Enviar movimiento de colita"}
                >
                  <span aria-hidden="true">🐾</span>
                  <span>
                    {alreadySent ? "Movimiento de colita enviado" : "Enviar movimiento de colita"}
                  </span>
                </Button>
                {alreadySent ? (
                  <p className="mt-3 text-center text-xs font-semibold text-emerald-700">
                    Cuando acepten, podrás conversar dentro de PawCita.
                  </p>
                ) : (
                  <p className="mt-3 flex items-center justify-center gap-2 text-center text-xs text-slate-400">
                    <span>🔒</span> Tu solicitud será enviada de manera segura.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Safety tips */}
          <div className="mt-5 rounded-3xl border border-amber-100 bg-amber-50/70 p-4 text-sm text-amber-900">
            <p className="font-bold">🐾 Encuentros responsables</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-900/90">
              <li>Recomendamos que el primer encuentro sea en un lugar público.</li>
              <li>Verifica el estado de salud de ambos perros antes del cruce.</li>
              <li>Consulta con tu veterinario antes de cruzar a tu perro.</li>
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
}

function InfoBox({ label, value, emoji }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-3 text-center shadow-sm">
      <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50 text-lg">
        {emoji}
      </div>
      <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </p>
      <p className="text-sm font-black text-[#0B2D4D]">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-[#FF5C5C]">
        {title}
      </p>
      {children}
    </div>
  );
}
